import {
  AccessLevel,
  Event,
  EventType,
  MembershipState,
  OnboardingFlowType,
  ThemeType,
  TicketType,
} from "@markit/common.types";
import { useCallback, useMemo, useState } from "react";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import {
  errorAlertText,
  errorSubAlertText,
  MEDIA_UPLOAD_CODES,
  uploadMedia,
} from "../../../../utils/photoUtils";
import ThemeVisibilityOptions from "../Components/ThemeVisibilityOptions";
import { FileUploader } from "react-drag-drop-files";
import AlertContainer from "../../../Containers/AlertContainer";
import RectangleButton from "../../../Buttons/RectangleButton";
import { StandardSectionContainer } from "../../../Containers/StandardSectionContainer";
import { DropdownMenu, DropdownMenuItem } from "../../../DropdownMenu";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { useSelector } from "react-redux";
import EventItemImage from "../../../DisplayItem/EventItem/EventItemImage";
import SwitchToggleItem from "../../../SwitchToggleItem";
import { isProductNonPostType } from "@markit/common.utils";
import EnablePaidTicketsModal from "../../../DisplayModal/EnablePaidTicketsModal";
import { getEventState } from "../../../../redux/slices/eventSlice";
import CreateEventTickets from "../../../CreateEvent/CreateEventTickets";
import FormBuilderWrapper from "../../../Form/FormBuilderWrapper";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import DropdownArrowIcon from "../../../DropdownsAndTabs/DropdownArrowIcon";
import OnboardingTourStep from "../../../Onboarding/OnboardingTourStep";
import { SELL_PRODUCTS_ONBOARDING_STEPS } from "../../../../utils/onboardingFlowUtils";

type CreateProductDetailsProps = {
  productDataSettings: Event;
  updateProductDataSettings: (update: Partial<Event>) => void;
  isEditing: boolean;
};

const CreateProductDetails = (props: CreateProductDetailsProps) => {
  const { productDataSettings, updateProductDataSettings, isEditing } = props;
  const { accountData, readyForPayment } = useSelector(getAccountState).account;
  const { events } = useSelector(getEventState).events;
  const [showFormQuestions, setShowFormQuestions] = useState(true);
  const [sellProductsTourStep, setSellProductsTourStep] = useState(3);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [connectStripeModal, setConnectStripeModal] = useState(false);
  const [showEnableMemmbershipAlert, setShowEnableMemmbershipAlert] =
    useState(false);
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({
    header: "",
    subHeader: "",
  });

  const styles = {
    iconContainer: {
      height: 26,
      width: 26,
      border: `0.5px solid ${Colors.GRAY2}`,
      borderRadius: 12,
      padding: 10,
      backgroundColor: Colors.WHITE,
    },
    imageContainer: {
      height: 200,
      width: 200,
      borderRadius: 10,
      backgroundColor: Colors.WHITE,
    },
    sectionContainer: {
      paddingBlock: 14,
      borderRadius: 20,
      backgroundColor: Colors.WHITE1,
      border: `1.5px solid ${Colors.GRAY11}`,
    },
    sectionItemContainer: {
      marginInline: 14,
      padding: 14,
      backgroundColor: Colors.WHITE,
      borderRadius: 12,
      border: `1.5px solid ${Colors.GRAY11}`,
    },
    priceContainer: { padding: "10px 14px", borderRadius: 12 },
  };

  const membershipsEnabled = useMemo(
    () =>
      accountData.membership &&
      accountData.membership.state === MembershipState.ACTIVE &&
      accountData.membership.membershipPlans,
    [accountData.membership]
  );

  const isEistingCheckoutProduct = useMemo(() => {
    const foundProduct = events.find(
      (product) => product.id === productDataSettings.id
    );
    return foundProduct && foundProduct.accessLevel === AccessLevel.CHECKOUT;
  }, [events, productDataSettings.id]);

  const uploadImage = useCallback(
    (file: File) => {
      return new Promise<void>((resolve, reject) => {
        (async () => {
          setUploadLoading(true);
          const uploadData = await uploadMedia(file, false);
          if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
            setAlertText({
              header: errorAlertText(uploadData.uploadCode),
              subHeader: errorSubAlertText(uploadData.uploadCode),
            });
            resolve();
          } else {
            updateProductDataSettings({
              photoURL: uploadData.blobUrl,
            });
            resolve();
          }
          setUploadLoading(false);
        })().catch(reject); // Handle any async errors
      });
    },
    [updateProductDataSettings]
  );

  const selectProductAccessLevel = useCallback(
    (accessLevel: AccessLevel) => {
      updateProductDataSettings({
        accessLevel,
        customTickets: [
          {
            ...productDataSettings.customTickets[0],
            type:
              accessLevel === AccessLevel.CHECKOUT
                ? TicketType.PAID
                : TicketType.FREE,
            price: accessLevel === AccessLevel.CHECKOUT ? 1000 : 0,
          },
        ],
      });
    },
    [productDataSettings.customTickets, updateProductDataSettings]
  );

  // If the current access level is checkout and there are orders sold, prevent user from switching
  const checkCheckoutOrdersSold = useCallback(
    (func: () => void) => {
      if (isEistingCheckoutProduct) {
        setAlertText({
          header: "Cannot Change Product Access",
          subHeader:
            "You cannot change existing products with a checkout product access. Create a new product if you want to have a different product access.",
        });
      } else {
        func();
      }
    },
    [isEistingCheckoutProduct]
  );

  const productAccessItems: DropdownMenuItem[] = useMemo(() => {
    const items = [
      {
        title: "Anyone",
        key: "anyone",
        onPress: () =>
          checkCheckoutOrdersSold(() =>
            selectProductAccessLevel(AccessLevel.ALL)
          ),
      },
    ];

    if (isProductNonPostType(productDataSettings.eventType)) {
      // Products - All, Checkout
      items.push({
        title: "Checkout",
        key: "checkout_only",
        onPress: () => selectProductAccessLevel(AccessLevel.CHECKOUT),
      });
    } else {
      // Posts - All, Subscribers, Paid Subscribers
      items.push(
        {
          title: "Subscribers Only",
          key: "subscribers_only",
          onPress: () =>
            checkCheckoutOrdersSold(() =>
              updateProductDataSettings({
                accessLevel: AccessLevel.SUBSCRIBERS,
              })
            ),
        },
        {
          title: "Paid Subscribers Only",
          key: "paid_subscribers_only",
          onPress: () =>
            checkCheckoutOrdersSold(() =>
              membershipsEnabled
                ? updateProductDataSettings({
                    accessLevel: AccessLevel.PAID_SUBSCRIBERS,
                  })
                : setShowEnableMemmbershipAlert(true)
            ),
        }
      );
    }

    return items;
  }, [
    checkCheckoutOrdersSold,
    membershipsEnabled,
    productDataSettings.eventType,
    selectProductAccessLevel,
    updateProductDataSettings,
  ]);

  return (
    <>
      <div
        className="ColumnNormal onboarding-post-details-target"
        style={{ gap: 24, marginInline: "auto", width: 583 }}
      >
        <StandardBorderedContainer containerStyles={styles.sectionContainer}>
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
              <div className="AlignedRow" style={{ gap: 10 }}>
                <div className="Centering" style={styles.iconContainer}>
                  <Icon
                    icon={"ion:bag-handle"}
                    height={25}
                    style={{ color: Colors.BLACK }}
                  />
                </div>
                <span className="bodyMedium">
                  {productDataSettings.eventType} Details
                </span>
              </div>
            </div>
            <div className="ColumnNormal" style={{ gap: 10 }}>
              <div style={{ paddingInline: 14 }}>
                <ThemeVisibilityOptions
                  productSettings={productDataSettings}
                  updateProductSettings={(update: Partial<Event>) => {
                    updateProductDataSettings({
                      ...productDataSettings,
                      ...update,
                    });
                  }}
                  containerStyles={{
                    backgroundColor: Colors.WHITE,
                  }}
                  alignment="row"
                />
              </div>
              {isProductNonPostType(productDataSettings.eventType) &&
              productDataSettings.eventType !== EventType.CUSTOM_PRODUCT ? (
                <StandardBorderedContainer
                  containerStyles={styles.sectionItemContainer}
                >
                  <SwitchToggleItem
                    title={"Allow Download"}
                    description={"Enable content to be downloadable by viewer"}
                    toggleValue={productDataSettings.mediaDownloadable}
                    onChange={() =>
                      updateProductDataSettings({
                        mediaDownloadable:
                          !productDataSettings.mediaDownloadable,
                      })
                    }
                  />
                </StandardBorderedContainer>
              ) : null}
              <StandardBorderedContainer
                containerStyles={{
                  ...styles.sectionItemContainer,
                  paddingInline: 0,
                }}
              >
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  <StandardSectionContainer
                    title={"Product Access"}
                    subtext={`Decide how this ${productDataSettings.eventType.toLowerCase()} can be accessed`}
                    onPress={() => {}}
                    containerStyles={{ paddingInline: 14 }}
                    icon={
                      <DropdownMenu
                        dropdownItems={productAccessItems}
                        trigger={["click"]}
                        placement="bottomRight"
                        altWidth={150}
                      >
                        <div className="AlignedRow" style={{ gap: 4 }}>
                          <span className="bodyMedium">
                            {productDataSettings.accessLevel === AccessLevel.ALL
                              ? "Anyone"
                              : productDataSettings.accessLevel ===
                                  AccessLevel.CHECKOUT
                                ? "Checkout"
                                : `${productDataSettings.accessLevel} Only`}
                          </span>
                          <Icon
                            icon={"ion:code"}
                            height={18}
                            style={{ transform: "rotate(90deg)" }}
                          />
                        </div>
                      </DropdownMenu>
                    }
                  />
                  {productDataSettings.accessLevel === AccessLevel.CHECKOUT ? (
                    <>
                      <div style={{ paddingInline: 14 }}>
                        <CreateEventTickets
                          eventSettings={productDataSettings}
                          updateEventSettings={updateProductDataSettings}
                          showConnectStripeModal={() =>
                            setConnectStripeModal(true)
                          }
                          readyForPayment={readyForPayment}
                          hardTheme={ThemeType.Light}
                          noHeader
                        />
                      </div>
                      <HorizontalDivider
                        altMargin={0}
                        eventTheme={ThemeType.Light}
                      />
                      <div
                        className="ColumnNormal"
                        style={{ gap: 14, paddingInline: 14 }}
                      >
                        <div className="AlignedRowSpaced">
                          <div className="AlignedRow" style={{ gap: 10 }}>
                            <div
                              className="Centering"
                              style={styles.iconContainer}
                            >
                              <Icon
                                icon={"ion:document-text"}
                                height={25}
                                style={{ color: Colors.BLACK }}
                              />
                            </div>
                            <div className="ColumnNormal" style={{ gap: 4 }}>
                              <span className="bodyMedium">Form Questions</span>
                              <span className="smallBodySubtext">
                                Require additional info to access product
                              </span>
                            </div>
                          </div>

                          <DropdownArrowIcon
                            isOpen={showFormQuestions}
                            onClick={() =>
                              setShowFormQuestions(!showFormQuestions)
                            }
                            color={Colors.GRAY1}
                            height={18}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                        {showFormQuestions ? (
                          <FormBuilderWrapper
                            isProfileQuestions={false}
                            hostId={accountData.uid}
                            event={productDataSettings}
                            updateEventSettings={updateProductDataSettings}
                            altHeader={<></>}
                            themeType={ThemeType.Light}
                          />
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </div>
              </StandardBorderedContainer>
            </div>
          </div>
        </StandardBorderedContainer>
        <StandardBorderedContainer
          className="onboarding-sell-products-socialpreview-target"
          containerStyles={styles.sectionContainer}
        >
          <div className="ColumnNormal" style={{ gap: 14, paddingInline: 14 }}>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodyMedium">Social Preview</span>
              <span className="smallBodySubtext">
                This only affects how your product appears in feeds and texts to
                followers.
              </span>
            </div>
            {productDataSettings.photoURL ? (
              <div
                className="RowNormal"
                style={{
                  cursor: "pointer",
                  position: "relative",
                  ...styles.imageContainer,
                }}
              >
                <EventItemImage
                  item={productDataSettings}
                  size={200}
                  scaleImage
                />
                <div
                  className="Centering"
                  style={{
                    position: "absolute",
                    right: 5,
                    top: 5,
                    backgroundColor: Colors.WHITE1,
                    border: `0.5px solid ${Colors.GRAY2}`,
                    borderRadius: 8,
                    height: 28,
                    width: 28,
                  }}
                  onClick={() => updateProductDataSettings({ photoURL: "" })}
                >
                  <Icon icon="ion:close" height={20} color={Colors.BLACK} />
                </div>
              </div>
            ) : (
              <FileUploader
                handleChange={uploadImage}
                name="file"
                types={["JPG", "JPEG", "PNG"]}
                onTypeError={() => {
                  setAlertText({
                    header: "Invalid File Type",
                    subHeader:
                      "Sorry, the uploaded file type is not supported. Please upload a .jpg, .jpeg, or .png file.",
                  });
                }}
                dropMessageStyle={{ opacity: 0.5 }}
                disabled={uploadLoading}
              >
                <div
                  className="ColumnCentering"
                  style={{
                    borderRadius: 20,
                    border: `1px solid ${Colors.GRAY2}`,
                    borderStyle: "dashed",
                    paddingBlock: 40,
                    gap: 10,
                    cursor: "pointer",
                  }}
                >
                  <Icon
                    icon="ion:image"
                    height={24}
                    style={{ color: Colors.BLACK }}
                  />
                  <span className="bodyMedium">Upload Image</span>
                  <span className="smallBodySubtext">
                    Ideal aspect ratio is 1:1
                  </span>
                  <div>
                    <RectangleButton
                      buttonLabel="Select File"
                      onPress={() => {}}
                      altColor={Colors.BLACK}
                      altTextColor={Colors.WHITE}
                      containerStyles={{
                        paddingInline: 14,
                        paddingBlock: 10,
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                </div>
              </FileUploader>
            )}
          </div>
        </StandardBorderedContainer>
      </div>
      {!isEditing &&
      productDataSettings.accessLevel === AccessLevel.CHECKOUT ? (
        sellProductsTourStep === 3 ? (
          <OnboardingTourStep
            key="sell-products-tour-step-3"
            flowType={OnboardingFlowType.SELL_PRODUCTS}
            target=".onboarding-sell-products-pricing-target"
            content={
              <>
                You can also add or customize order options here, each with its
                own price, available quantity, and other details for buyers to
                choose from.
                {!readyForPayment ? (
                  <>
                    <br />
                    <br />
                    You&apos;ll need to connect Stripe to accept payments before
                    you can publish.
                  </>
                ) : null}
              </>
            }
            stepNumber={3}
            totalSteps={SELL_PRODUCTS_ONBOARDING_STEPS}
            placement="right"
            onStepComplete={() => setSellProductsTourStep(4)}
          />
        ) : sellProductsTourStep === 4 ? (
          <OnboardingTourStep
            key="sell-products-tour-step-4"
            flowType={OnboardingFlowType.SELL_PRODUCTS}
            target=".onboarding-sell-products-questions-target"
            content="Add questions to collect additional information from people before they can access your product."
            stepNumber={4}
            totalSteps={SELL_PRODUCTS_ONBOARDING_STEPS}
            placement="right"
            onStepComplete={() => setSellProductsTourStep(5)}
          />
        ) : sellProductsTourStep === 5 ? (
          <OnboardingTourStep
            key="sell-products-tour-step-5"
            flowType={OnboardingFlowType.SELL_PRODUCTS}
            target=".onboarding-sell-products-socialpreview-target"
            content={
              <>
                We recommend having a social preview image so your product
                displays a link preview when shared in texts or on social media.
              </>
            }
            stepNumber={5}
            totalSteps={SELL_PRODUCTS_ONBOARDING_STEPS}
            placement="left"
            onStepComplete={() => setSellProductsTourStep(6)}
          />
        ) : (
          <OnboardingTourStep
            key="sell-products-tour-step-6"
            flowType={OnboardingFlowType.SELL_PRODUCTS}
            target=".onboarding-sell-products-publish-target"
            content={
              <>
                A text automation will be created automatically upon publishing,
                which you can customize from the product dashboard.
              </>
            }
            stepNumber={6}
            totalSteps={SELL_PRODUCTS_ONBOARDING_STEPS}
            placement="bottom"
          />
        )
      ) : null}
      <AlertContainer
        headerComp={<span className="sectionTitle">{alertText.header}</span>}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <Icon icon="ion:ribbon" height={40} />
            <span className="sectionTitle">Enable Paid Memberships</span>
          </div>
        }
        subHeaderComp={
          "To select this access level, you must enable paid memberships. You can enable this in the Memberships tab."
        }
        closeModal={() => setShowEnableMemmbershipAlert(false)}
        hideModal={!showEnableMemmbershipAlert}
      />
      {connectStripeModal ? (
        <EnablePaidTicketsModal
          isVisible={connectStripeModal}
          setIsVisible={setConnectStripeModal}
        />
      ) : null}
    </>
  );
};

export default CreateProductDetails;
